/* eslint-disable no-nested-ternary */
/* eslint-disable max-lines */
/* eslint-disable import/prefer-default-export */
/* eslint-disable complexity */
/* eslint-disable max-statements */
import { useCallback, useMemo } from 'react';
import type { useChangeHandlers } from './useChangeHandlers.js';
import type { useGetPropsBasedOnType } from './useGetPropsBasedOnType.js';
import type { useGetReferences } from './useGetReferences.js';
import type { useGetFlags } from './useGetFlags.js';
import type { useFocusTracker } from './useFocusTracker.js';
import { getValidationDateStringMetaInfo, getDateStringFromDay, getTimeStringFromDay } from '../utils/dateHelpers.js';
import { getValidationTimeStringMetaInfo } from '../utils/timeHelpers.js';
import { deconstructValuesFromDateString, deconstructValuesFromTimeString } from '../utils/stringHelpers.js';
import type { DSControlledDateTimePickerT } from '../react-desc-prop-types.js';

interface Config {
  propsBasedOnType: ReturnType<typeof useGetPropsBasedOnType>;
  changeHandlers: ReturnType<typeof useChangeHandlers>;
  DOMRefs: ReturnType<typeof useGetReferences>;
  flags: ReturnType<typeof useGetFlags>;
  focusTrackers: ReturnType<typeof useFocusTracker>;
}

export const useGlobalKeyHandlers = ({ propsBasedOnType, changeHandlers, focusTrackers, flags }: Config) => {
  const {
    getIsOutOfRangeDay,
    getIsDisabledDay,
    getIsDisabledTime,
    appOnMonthChange,
    appOnDayChange,
    appOnYearChange,
    appOnHourChange,
    appOnMinuteChange,
    appOnMeridiemChange,
  } = propsBasedOnType;
  const { handleChangeComposedDateTimeString, handleClearAll } = changeHandlers;
  const { trackFocusLastSegment, trackFocusClearBtn } = focusTrackers;
  const { withAnyPicker, shouldPreserveClearableSpace } = flags;

  const onFillWithCurrentDateKeys = useCallback(
    (
      e: React.KeyboardEvent<HTMLDivElement>,
      metaInfo: Partial<DSControlledDateTimePickerT.InternalInputsChangeMetaInfo> = { isAutomaticFillTrigger: true },
    ) => {
      const now = new Date();
      const nowDateString = getDateStringFromDay(now);
      const nowTimeString = getTimeStringFromDay(now);
      const finalMetaInfo: DSControlledDateTimePickerT.InternalInputsChangeMetaInfo = {
        ...metaInfo,
        ...getValidationDateStringMetaInfo(nowDateString, getIsOutOfRangeDay, getIsDisabledDay),
        ...getValidationTimeStringMetaInfo(nowTimeString, getIsDisabledTime),
        newDateString: nowDateString,
      };
      const { month, day, year } = deconstructValuesFromDateString(nowDateString);
      appOnMonthChange(month, e, metaInfo);
      appOnDayChange(day, e, metaInfo);
      appOnYearChange(year, e, metaInfo);
      const { hours, minutes, meridiem } = deconstructValuesFromTimeString(nowTimeString);
      appOnHourChange(hours, e, metaInfo);
      appOnMinuteChange(minutes, e, metaInfo);
      appOnMeridiemChange(meridiem, e, metaInfo);
      handleChangeComposedDateTimeString(nowDateString, nowTimeString, finalMetaInfo);
      // trackFocusLastSegment is not enough here,
      //  after this key combination the value may change from empty to non-empty
      //  when non empty the last segment may become the clear-btn (clear btn only appears when non empty)
      //  as such this must be micro-managed here
      if (shouldPreserveClearableSpace && !withAnyPicker) trackFocusClearBtn();
      else trackFocusLastSegment();
    },
    [
      appOnDayChange,
      appOnHourChange,
      appOnMeridiemChange,
      appOnMinuteChange,
      appOnMonthChange,
      appOnYearChange,
      getIsDisabledDay,
      getIsDisabledTime,
      getIsOutOfRangeDay,
      handleChangeComposedDateTimeString,
      shouldPreserveClearableSpace,
      trackFocusClearBtn,
      trackFocusLastSegment,
      withAnyPicker,
    ],
  );

  const onGlobalKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const { key, ctrlKey, metaKey } = e;
      if ((ctrlKey || metaKey) && key === ';') {
        onFillWithCurrentDateKeys(e);
      }
      if ((ctrlKey || metaKey) && (key === 'Delete' || key === 'Backspace')) handleClearAll(e);
    },
    [handleClearAll, onFillWithCurrentDateKeys],
  );

  return useMemo(
    () => ({
      onFillWithCurrentDateKeys,
      onGlobalKeyDown,
    }),
    [onFillWithCurrentDateKeys, onGlobalKeyDown],
  );
};
